import React from 'react';
import {
  StepCard,
  BulletList,
  NumberedList,
  Callout,
  Connector,
  VideoIcon,
  PhoneIcon,
  PhoneCallIcon,
  LockIcon,
  CalendarIcon,
  CheckIcon,
} from './guide_primitives';

import OptionTabs from './option_tabs';

import styles from './index.module.css';

export const PATIENT_GUIDE_EYEBROW = 'How Calls Work';
export const PATIENT_GUIDE_INTRO = 'How mentor calls work';
export const PATIENT_GUIDE_INTRO_SUB =
  'Learn what happens when you request a call with a mentor. The process is simple, flexible, and always in your control.';
const PATIENT_VIDEO_URL = 'https://patientpartner-images2.s3.us-west-1.amazonaws.com/website/phone_call_patient.mp4';

function VideoCard() {
  return (
    <div className={styles.video}>
      <div className={styles.video_head}>
        <span className={styles.video_dot} aria-hidden='true' />
        <p className={styles.video_head_label}>Watch this first</p>
      </div>
      <div className={styles.video_body}>
        <video className={styles.video_media} src={PATIENT_VIDEO_URL} controls playsInline preload='metadata' />
      </div>
    </div>
  );
}

export default function MenteeGuide() {
  return (
    <>
      <StepCard
        variant='white'
        icon={<VideoIcon />}
        label='Step 1'
        title='First, watch this quick video'
        description='Watch this short walkthrough of how mentor calls work. The steps below cover everything too, so read or watch- whatever you prefer.'
        aside={<VideoCard />}
      />

      <Connector label='REQUESTING A CALL' />

      <StepCard
        variant='white'
        icon={<PhoneIcon />}
        label='Step 2'
        title='Request a call'
        description="When you're ready to speak with a mentor, select Request a Call in your conversation. We'll immediately notify your mentor and let them know you'd like to connect."
        aside={
          <>
            <h3 className={styles.step_title}>Key things to know:</h3>
            <BulletList
              items={[
                "Call requests can be made between 8:00 AM and 8:00 PM in your mentor's time zone.",
                'Your mentor has 15 minutes to accept the request.',
                "If they're unavailable, you can schedule a time that works for both of you.",
                'Your personal phone number is never shared.',
              ]}
            />
          </>
        }
        callout={
          <Callout
            title="What if my mentor isn't available?"
            body="No problem. If your mentor can't accept the request right away, you can schedule a time to connect later or submit another request when you're ready."
          />
        }
      />

      <Connector label='CHOOSING A TIME' />

      <OptionTabs
        options={[
          {
            kicker: 'Option 1',
            name: 'Connect now',
            icon: <PhoneCallIcon />,
            content: (
              <>
                <NumberedList
                  items={[
                    'Request a call.',
                    'If your mentor accepts, your phone will ring.',
                    'Press 1 to join the call.',
                    'Once connected, you can begin your conversation.',
                  ]}
                />
                <Callout
                  title='What if I miss the call?'
                  body="That's okay. You can simply request another call when you're ready."
                />
              </>
            ),
          },
          {
            kicker: 'Option 2',
            name: 'Schedule for later',
            icon: <CalendarIcon />,
            content: (
              <>
                <NumberedList
                  items={[
                    'Suggest a few times that work for you.',
                    'Your mentor can choose one, suggest alternatives, or decline.',
                    "Once a time is confirmed, you'll receive reminders before the call",
                    "When it's time, your phone will ring. Press 1 to join the call.",
                  ]}
                />
                <Callout
                  title='Need a different time?'
                  body='You and your mentor can suggest alternative times until you find one that works for both of you.'
                />
              </>
            ),
          },
        ]}
      />

      <Connector label='HAVING THE CONVERSATION' />

      <StepCard
        variant='white'
        icon={<LockIcon />}
        label='Step 3'
        title='Have the conversation'
        description="Once connected, you'll speak by phone just like any regular call. Your phone number remains private throughout the conversation."
        footerTitle='About your mentor'
        footerDescription='Your mentor has personal experience with a similar condition, treatment, or caregiving journey. They are here to share their personal experience and perspective.'
        subFooterTitle='What can I ask?'
        subFooterDescription="You can ask about your mentor's personal experience, including what their journey was like, challenges they faced, questions they had, and what they learned along the way."
        aside={
          <>
            <h3 className={styles.step_title}>Key things to know:</h3>

            <BulletList
              items={[
                'No special app or equipment is required.',
                'Most conversations last about 15-30 minutes, but there is no required call length.',
                'Either you or your mentor may end the conversation at any time.',
                "You're always in control of your participation.",
              ]}
            />
          </>
        }
      />

      <div className='marg-top-20'>
        <Callout
          title='Reminder'
          body='Mentors share their own experiences and perspectives. They do not provide medical advice, treatment recommendations, or clinical guidance. Always speak with your healthcare team regarding medical questions or treatment decisions.'
        />
      </div>
    </>
  );
}
